class EverideDetailVideo extends HTMLElement {
  #loadObserver;
  #playbackObserver;
  #motionQuery;
  #toggleButton;
  #video;
  #isLoaded = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#toggleButton = this.querySelector('[data-video-toggle]');
    if (!this.#video || !this.#toggleButton) return;

    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#toggleButton.addEventListener('click', this.#handleToggle);
    this.#video.addEventListener('play', this.#syncControl);
    this.#video.addEventListener('pause', this.#syncControl);
    this.#motionQuery.addEventListener('change', this.#handleMotionChange);

    this.#loadObserver = new IntersectionObserver(this.#handleLoadIntersection, {
      rootMargin: '300px 0px',
      threshold: 0,
    });
    this.#playbackObserver = new IntersectionObserver(this.#handlePlaybackIntersection, {
      threshold: 0.15,
    });
    this.#loadObserver.observe(this);
    this.#playbackObserver.observe(this);
    this.#syncControl();
  }

  disconnectedCallback() {
    this.#loadObserver?.disconnect();
    this.#playbackObserver?.disconnect();
    this.#toggleButton?.removeEventListener('click', this.#handleToggle);
    this.#video?.removeEventListener('play', this.#syncControl);
    this.#video?.removeEventListener('pause', this.#syncControl);
    this.#motionQuery?.removeEventListener('change', this.#handleMotionChange);
    this.#video?.pause();
  }

  #handleLoadIntersection = (entries) => {
    const entry = entries[0];
    if (!entry?.isIntersecting || this.#isLoaded) return;

    this.#video.load();
    this.#isLoaded = true;
    this.#loadObserver?.disconnect();
  };

  #handlePlaybackIntersection = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    if (!entry.isIntersecting) {
      this.#video.pause();
      return;
    }

    if (!this.#isLoaded) {
      this.#video.load();
      this.#isLoaded = true;
    }
    if (!this.#motionQuery.matches) {
      this.#video.play().catch(() => this.#syncControl());
    }
  };

  #handleToggle = () => {
    if (!this.#isLoaded) {
      this.#video.load();
      this.#isLoaded = true;
    }

    if (this.#video.paused) {
      this.#video.play().catch(() => this.#syncControl());
    } else {
      this.#video.pause();
    }
  };

  #handleMotionChange = (event) => {
    if (event.matches) this.#video.pause();
  };

  #syncControl = () => {
    const isPlaying = !this.#video.paused;
    const label = isPlaying ? this.#toggleButton.dataset.pauseLabel : this.#toggleButton.dataset.playLabel;
    this.dataset.playing = String(isPlaying);
    this.#toggleButton.setAttribute('aria-label', label);

    const labelNode = this.#toggleButton.querySelector('[data-video-toggle-label]');
    if (labelNode) labelNode.textContent = label;
  };
}

class EverideProductFaq extends HTMLElement {
  #buttons = [];

  connectedCallback() {
    this.#buttons = [...this.querySelectorAll('[data-faq-trigger]')];
    for (const button of this.#buttons) {
      button.addEventListener('click', this.#handleToggle);
      this.#setExpanded(button, button.getAttribute('aria-expanded') === 'true');
    }
  }

  disconnectedCallback() {
    for (const button of this.#buttons) {
      button.removeEventListener('click', this.#handleToggle);
    }
    this.#buttons = [];
  }

  #handleToggle = (event) => {
    const button = event.currentTarget;
    this.#setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
  };

  #setExpanded(button, expanded) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    button.setAttribute('aria-expanded', String(expanded));
    if (panel) panel.hidden = !expanded;
  }
}

class EverideFeatureCarousel extends HTMLElement {
  #track;
  #previousButton;
  #nextButton;
  #resizeObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-feature-track]');
    this.#previousButton = this.querySelector('[data-feature-scroll-previous]');
    this.#nextButton = this.querySelector('[data-feature-scroll-next]');
    if (!this.#track || !this.#previousButton || !this.#nextButton) return;

    this.#previousButton.addEventListener('click', this.#handlePrevious);
    this.#nextButton.addEventListener('click', this.#handleNext);
    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#syncControls);
    this.#resizeObserver.observe(this.#track);
    requestAnimationFrame(this.#syncControls);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#previousButton?.removeEventListener('click', this.#handlePrevious);
    this.#nextButton?.removeEventListener('click', this.#handleNext);
    this.#track?.removeEventListener('scroll', this.#handleScroll);
  }

  #handlePrevious = () => this.#scrollByCard(-1);

  #handleNext = () => this.#scrollByCard(1);

  #handleScroll = () => requestAnimationFrame(this.#syncControls);

  #scrollByCard(direction) {
    const card = this.#track.querySelector('.everide-feature-card');
    if (!card) return;

    const styles = getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const distance = card.getBoundingClientRect().width + gap;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollBy({
      left: distance * direction,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  #syncControls = () => {
    if (!this.#track || !this.#previousButton || !this.#nextButton) return;

    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const currentScroll = Math.max(0, this.#track.scrollLeft);
    this.#previousButton.disabled = currentScroll <= 2;
    this.#nextButton.disabled = currentScroll >= maxScroll - 2;
  };
}

if (!customElements.get('everide-detail-video')) {
  customElements.define('everide-detail-video', EverideDetailVideo);
}

if (!customElements.get('everide-product-faq')) {
  customElements.define('everide-product-faq', EverideProductFaq);
}

if (!customElements.get('everide-feature-carousel')) {
  customElements.define('everide-feature-carousel', EverideFeatureCarousel);
}
